import {isAttendee, useEventAuth} from 'Event/auth'
import {replace, parseVariables} from 'lib/template'
import {
  ISO_MINUTE_FORMAT,
  browserTimezone,
  localize,
  time,
  today,
} from 'lib/date-time'
import moment from 'moment-timezone'
import {useCallback} from 'react'
import {Score, usePoints} from 'Event/PointsProvider'
import {useTemplate} from 'Event/TemplateProvider'
import {useEvent} from 'Event/EventProvider'
import {useEditMode} from 'Event/EditModeProvider'
import {useOptionalPreviewAttendeeProfile} from 'Event/attendee-rules/AttendeeProfileProvider/PreviewAttendeeProfileProvider'

/**
 * Dynamically replace text with data from the currently authenticated
 * Attendee. Variables are defined using {{double braces}}.
 *
 * @returns
 */

export function useWithAttendeeData() {
  const {user} = useEventAuth()
  const isEditMode = useEditMode()
  const {
    event: {name: eventName, timezone: eventTimezone},
  } = useEvent()
  const previewProfile = useOptionalPreviewAttendeeProfile()

  return useCallback(
    (text: string) => {
      const hasText = Boolean(text)

      if (!hasText) {
        return text
      }

      if (isEditMode && !isAttendee(user)) {
        return text
      }

      let result = replace('today', today(), text)
      result = replace('time', time(), result)
      result = replace('event', eventName, result)
      result = result.replace(
        /{{custom_date_time\|([^|}]+)\|([01])}}/gi,
        (_match, date, flag) => {
          const timezone = eventTimezone ?? browserTimezone()

          return localize({
            date: moment.tz(date, ISO_MINUTE_FORMAT, timezone).toISOString(),
            timezone: flag === '1' ? browserTimezone() : timezone,
          })
        },
      )

      // If editing event (ie. host) we won't have an attendee user
      if (!isAttendee(user)) {
        const previewVariables = {
          'first name': previewProfile?.firstName,
          'last name': previewProfile?.lastName,
          first_name: previewProfile?.firstName,
          last_name: previewProfile?.lastName,
          email: previewProfile?.email,
        }

        return Object.entries(previewVariables).reduce(
          (previewResult, [key, value]) => replace(key, value, previewResult),
          result,
        )
      }

      /**
       * Known variables that we'll try to replace first. Anything
       * not defined here we'll assume are custom group
       * keys.
       */

      const baseAttributes = {
        // For backwards compatibility. At some point in the future we can remove
        // this "spaced" replacement, once we feel content that has been created
        // with the "spaced" version is no longer relevant.
        'first name': user.first_name,
        'last name': user.last_name,
        // Consistent name formats.
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        login_url: user.login_url,
      }

      /**
       * Check if a key is a base attribute
       */

      const isBaseAttribute = (key: string) =>
        Object.keys(baseAttributes).includes(key)

      /**
       * Parse custom variables keys within a text
       */

      const customVariables = (text: string) => {
        return parseVariables(text).filter((v) => !isBaseAttribute(v))
      }

      /**
       * Replace base attributes
       */

      for (const [key, value] of Object.entries(baseAttributes)) {
        result = replace(key, value, result)
      }

      /**
       * Replace group values
       */

      for (const key of customVariables(result)) {
        const groupKey = Object.keys(user.groups).find(
          (userGroupKey) => userGroupKey.toLowerCase() === key.toLowerCase(),
        )

        if (!groupKey) {
          continue
        }

        const value = user.groups[groupKey]
        if (!value) {
          continue
        }

        result = replace(key, String(value), result)
      }

      return result
    },
    [user, isEditMode, eventName, eventTimezone, previewProfile],
  )
}

/**
 *
 * @returns
 */
export function useWithPoints() {
  const {score} = usePoints()
  return useWithStaticPoints(score)
}

export function useWithStaticPoints(score: Score) {
  const template = useTemplate()
  const {points_unit} = template

  return useCallback(
    (text: string) => {
      if (!text) {
        return text
      }

      const variables = {
        leaderboard_points: String(score.points),
        leaderboard_position: String(score.position),
        points_unit,
      }

      let result = text

      for (const [key, value] of Object.entries(variables)) {
        result = replace(key, value, result)
      }

      return result
    },
    [score, points_unit],
  )
}
